export interface TrendingPrompt {
  id: string;
  category: string;
  title: string;
  prompt: string;
  tags: string[];
  estimatedQuality: 'standard' | 'hd';
  suggestedSize: string;
}

export const promptCategories = [
  'All',
  'Photography & Realism',
  'Digital Art & Illustration',
  '3D & CGI',
  'Fantasy & Sci-Fi',
  'Minimalist & Abstract'
] as const;

export const trendingPrompts: TrendingPrompt[] = [
  // Photography & Realism
  {
    id: 'photo-1',
    category: 'Photography & Realism',
    title: 'Luxury Product Photography',
    prompt: 'Professional product photography of a luxury watch on black velvet, studio lighting, macro lens, shallow depth of field, 8K resolution, commercial quality',
    tags: ['trending', 'product'],
    estimatedQuality: 'hd',
    suggestedSize: '1024x1024'
  },
  {
    id: 'photo-2',
    category: 'Photography & Realism',
    title: 'Golden Hour Portrait',
    prompt: 'Cinematic portrait of a person in golden hour lighting, shot on film, bokeh background, warm tones, professional photography, natural beauty',
    tags: ['popular', 'portrait'],
    estimatedQuality: 'hd',
    suggestedSize: '1024x1792'
  },
  {
    id: 'photo-3',
    category: 'Photography & Realism',
    title: 'Aerial Autumn Road',
    prompt: 'Aerial drone photograph of a winding mountain road through autumn forest, vibrant fall colors, misty morning, 4K quality, landscape photography',
    tags: ['trending', 'landscape'],
    estimatedQuality: 'standard',
    suggestedSize: '1792x1024'
  },
  {
    id: 'photo-4',
    category: 'Photography & Realism',
    title: 'Food Photography',
    prompt: 'Professional food photography of gourmet burger with melting cheese, dramatic side lighting, rustic wooden table, shallow depth of field, appetizing presentation',
    tags: ['popular', 'food'],
    estimatedQuality: 'hd',
    suggestedSize: '1024x1024'
  },

  // Digital Art & Illustration
  {
    id: 'art-1',
    category: 'Digital Art & Illustration',
    title: 'Cyberpunk Cityscape',
    prompt: 'Vibrant digital illustration of a cyberpunk cityscape at night, neon lights, rain-soaked streets, detailed architecture, flying vehicles, trending on ArtStation',
    tags: ['trending', 'cyberpunk'],
    estimatedQuality: 'standard',
    suggestedSize: '1792x1024'
  },
  {
    id: 'art-2',
    category: 'Digital Art & Illustration',
    title: 'Magical Forest',
    prompt: 'Whimsical watercolor painting of a magical forest with glowing mushrooms, soft pastel colors, dreamy atmosphere, children\'s book style, enchanting details',
    tags: ['popular', 'fantasy'],
    estimatedQuality: 'standard',
    suggestedSize: '1024x1792'
  },
  {
    id: 'art-3',
    category: 'Digital Art & Illustration',
    title: 'Isometric Coffee Shop',
    prompt: 'Isometric 3D illustration of a cozy coffee shop interior, warm lighting, detailed furniture, potted plants, low poly style, cute aesthetic',
    tags: ['trending', 'isometric'],
    estimatedQuality: 'standard',
    suggestedSize: '1024x1024'
  },
  {
    id: 'art-4',
    category: 'Digital Art & Illustration',
    title: 'Anime Character Portrait',
    prompt: 'Beautiful anime character portrait, detailed eyes, flowing hair, vibrant colors, Studio Ghibli style, soft lighting, professional illustration',
    tags: ['popular', 'anime'],
    estimatedQuality: 'hd',
    suggestedSize: '1024x1792'
  },

  // 3D & CGI
  {
    id: '3d-1',
    category: '3D & CGI',
    title: 'Futuristic Sports Car',
    prompt: 'Photorealistic 3D render of a futuristic sports car, metallic blue paint, studio lighting, reflections on wet floor, Octane render, 8K quality',
    tags: ['trending', 'automotive'],
    estimatedQuality: 'hd',
    suggestedSize: '1792x1024'
  },
  {
    id: '3d-2',
    category: '3D & CGI',
    title: 'Modern Architecture',
    prompt: 'High-quality 3D architectural visualization of a modern minimalist house, glass walls, natural lighting, lush garden, contemporary design, photorealistic',
    tags: ['popular', 'architecture'],
    estimatedQuality: 'hd',
    suggestedSize: '1792x1024'
  },
  {
    id: '3d-3',
    category: '3D & CGI',
    title: 'Product Render - Earbuds',
    prompt: '3D product render of wireless earbuds floating in air, clean white background, soft shadows, commercial quality, minimalist composition',
    tags: ['trending', 'product'],
    estimatedQuality: 'standard',
    suggestedSize: '1024x1024'
  },
  {
    id: '3d-4',
    category: '3D & CGI',
    title: 'Abstract Sculpture',
    prompt: 'Abstract 3D sculpture with flowing organic shapes, iridescent metallic material, dramatic lighting, artistic composition, modern art',
    tags: ['new', 'abstract'],
    estimatedQuality: 'standard',
    suggestedSize: '1024x1024'
  },

  // Fantasy & Sci-Fi
  {
    id: 'fantasy-1',
    category: 'Fantasy & Sci-Fi',
    title: 'Floating Islands',
    prompt: 'Epic fantasy landscape with floating islands, waterfalls cascading into clouds, dragons flying, magical atmosphere, concept art quality, dramatic sky',
    tags: ['trending', 'fantasy'],
    estimatedQuality: 'standard',
    suggestedSize: '1792x1024'
  },
  {
    id: 'fantasy-2',
    category: 'Fantasy & Sci-Fi',
    title: 'Space Station',
    prompt: 'Sci-fi space station orbiting an alien planet, detailed mechanical structures, stars and nebula in background, cinematic lighting, futuristic design',
    tags: ['popular', 'sci-fi'],
    estimatedQuality: 'standard',
    suggestedSize: '1792x1024'
  },
  {
    id: 'fantasy-3',
    category: 'Fantasy & Sci-Fi',
    title: 'Crystal Cave',
    prompt: 'Mystical crystal cave with bioluminescent plants, ethereal glow, fantasy RPG concept art, highly detailed, magical ambiance, purple and blue tones',
    tags: ['trending', 'fantasy'],
    estimatedQuality: 'standard',
    suggestedSize: '1024x1792'
  },
  {
    id: 'fantasy-4',
    category: 'Fantasy & Sci-Fi',
    title: 'Cybernetic Warrior',
    prompt: 'Futuristic cybernetic warrior in neon-lit alley, advanced armor, glowing elements, rain effects, cinematic composition, sci-fi character design',
    tags: ['new', 'character'],
    estimatedQuality: 'hd',
    suggestedSize: '1024x1792'
  },

  // Minimalist & Abstract
  {
    id: 'minimal-1',
    category: 'Minimalist & Abstract',
    title: 'Geometric Abstract',
    prompt: 'Minimalist geometric abstract art, pastel gradient colors, smooth shapes, modern aesthetic, 3D render, clean composition',
    tags: ['trending', 'minimal'],
    estimatedQuality: 'standard',
    suggestedSize: '1024x1024'
  },
  {
    id: 'minimal-2',
    category: 'Minimalist & Abstract',
    title: 'Fluid Art',
    prompt: 'Abstract fluid art with metallic gold and deep blue swirls, luxury aesthetic, high-end design, marble texture, elegant composition',
    tags: ['popular', 'abstract'],
    estimatedQuality: 'standard',
    suggestedSize: '1024x1024'
  },
  {
    id: 'minimal-3',
    category: 'Minimalist & Abstract',
    title: 'Product Mockup Scene',
    prompt: 'Clean minimal product mockup scene, soft shadows, neutral beige tones, professional commercial photography, simple elegant composition',
    tags: ['trending', 'mockup'],
    estimatedQuality: 'standard',
    suggestedSize: '1024x1024'
  },
  {
    id: 'minimal-4',
    category: 'Minimalist & Abstract',
    title: 'Gradient Waves',
    prompt: 'Smooth gradient waves in purple and blue, minimalist abstract design, modern digital art, calming aesthetic, wallpaper quality',
    tags: ['new', 'wallpaper'],
    estimatedQuality: 'standard',
    suggestedSize: '1792x1024'
  }
];
